'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { useState } from 'react';
import ActionWorldCardBadgeList from '@/components/ActionWorldCardBadgeList';
import OptimizedImage from '@/components/OptimizedImage';
import PremiumBadge from '@/components/PremiumBadge';
import VerifiedBadge from '@/components/VerifiedBadge';
import { SearchContentType } from '@/app/[locale]/(worlds)/(worlds-landing)/search/components/useWorldsSearch';
import IconExperimence from '@/components/assets/icon_experimence.svg';
import IconEye from '@/components/assets/icon_eye.svg';
import IconGamepad from '@/components/assets/icon_gamepad.svg';
import IconLike from '@/components/assets/icon_like.svg';
import IconPlay from '@/components/assets/icon_play.svg';
import ViverseSkeleton from '@/components/ViverseSkeleton';
import { useFormatVODTime } from '@/hooks/useFormatVODTime';
import { Link } from '@/i18n/navigation';
import { hasVerifiedCreatorBadge } from '@/lib/cms';
import { formatDuration } from '@/lib/format';
import { numberFormatter } from '@/lib/formatter';
import { cn } from '@/lib/utils';
import roomDefaultImage from '@/public/images/room-default.png';
import { Room } from '@/services/cms';
const HoverVideoPreview = dynamic(() => import('@/components/HoverVideoPreview'), {
  ssr: false
});
export const convertRoomToRoomCardFormat = (room: Room) => ({
  id: Number.parseInt(room.id) || 0,
  name: room.title,
  creator: room.owner?.display_name || '',
  communityUserId: room.owner?.community_user_id,
  ownerIsVerified: hasVerifiedCreatorBadge(room.owner),
  thumbnail: room.image || roomDefaultImage.src,
  views: numberFormatter(room.view_count ?? 0),
  likes: numberFormatter(room.like_count ?? 0),
  description: room.description_plaintext || room.description || '',
  roomId: room.hub_sid,
  vodDuration: room.vod_duration,
  vodLastUpdate: room.vod_last_update_date,
  vodId: room.vod_id || room.trailer_videos?.[0]?.vod_id,
  isVoucherRoom: room.is_voucher_room,
  isExclusive: room.is_exclusive,
  isSelected: room.is_selected,
  isHot: room.is_hot,
  isTop: room.is_top
});
type RoomCardRoom = ReturnType<typeof convertRoomToRoomCardFormat>;
type RoomCardProps = {
  room: RoomCardRoom;
  contentType?: SearchContentType;
};
export default function RoomCard(t0) {
  const $ = _c(85);
  const {
    room,
    contentType
  } = t0;
  const isVOD = typeof room.vodDuration === "number" && !!room.vodLastUpdate;
  const showPremiumTag = !!room.isVoucherRoom;
  const formatVODTime = useFormatVODTime();
  const [isHovered, setIsHovered] = useState(false);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsHovered(true);
    t2 = () => setIsHovered(false);
    t3 = () => setIsHovered(true);
    t4 = () => setIsHovered(false);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  const t5 = `/${room.roomId}`;
  let t6;
  if ($[4] !== room.name || $[5] !== t5) {
    t6 = <Link href={t5} className="z-interactive absolute inset-0" aria-label={room.name} />;
    $[4] = room.name;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = `/${room.roomId}`;
  let t8;
  if ($[7] !== contentType || $[8] !== isHovered || $[9] !== room.vodId) {
    t8 = isHovered && room.vodId && contentType === "videos" && <div className="fade-in animate-in z-interactive absolute inset-0 duration-300"><HoverVideoPreview vodId={room.vodId} videoClassName="rounded-[15px] lg:rounded-lg" /></div>;
    $[7] = contentType;
    $[8] = isHovered;
    $[9] = room.vodId;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== room.name || $[12] !== room.thumbnail) {
    t9 = <OptimizedImage src={room.thumbnail} alt={room.name} width={306} height={178} className="1.5xl:min-w-[306px] aspect-306/178 w-[171px] shrink-0 rounded-[15px] object-cover group-hover:shadow-lg md:w-32 lg:min-w-[200px] lg:rounded-lg" />;
    $[11] = room.name;
    $[12] = room.thumbnail;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== room.name || $[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <Link href={t7} className="z-floating relative block" aria-label={room.name}>{t8}{t9}</Link>;
    $[14] = room.name;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== showPremiumTag) {
    t11 = showPremiumTag && <div className="z-floating pointer-events-none absolute top-1.5 right-2"><PremiumBadge showLabel={true} className="max-lg:w-[34px] max-lg:px-0 max-lg:[&_span]:hidden" /></div>;
    $[19] = showPremiumTag;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== contentType || $[22] !== room.isExclusive || $[23] !== room.isHot || $[24] !== room.isSelected || $[25] !== room.isTop) {
    t12 = <div className="z-floating pointer-events-none absolute top-1.5 left-2 max-lg:[&_[role=img]]:size-6 max-lg:[&_[role=img]]:p-1 max-lg:[&_span]:hidden"><ActionWorldCardBadgeList inline={true} isExclusive={room.isExclusive} isSelected={room.isSelected} isHot={room.isHot} isTop={room.isTop} cardType={contentType} /></div>;
    $[21] = contentType;
    $[22] = room.isExclusive;
    $[23] = room.isHot;
    $[24] = room.isSelected;
    $[25] = room.isTop;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== contentType || $[28] !== isVOD || $[29] !== room.vodDuration) {
    t13 = isVOD && room.vodDuration && contentType === "videos" && <div className="z-floating pointer-events-none absolute right-2 bottom-2 rounded-full bg-[#00000099] px-2 text-white">{formatDuration(room.vodDuration)}</div>;
    $[27] = contentType;
    $[28] = isVOD;
    $[29] = room.vodDuration;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== contentType || $[32] !== isHovered) {
    t14 = contentType === "videos" && <div className={cn("z-floating pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 ease-out", isHovered ? "opacity-0" : "opacity-100")}><div className="flex items-center justify-center rounded-full bg-white p-4 drop-shadow-[0px_0px_6px_rgba(0,0,0,0.55)]"><IconPlay className="size-[15px] fill-black" /></div></div>;
    $[31] = contentType;
    $[32] = isHovered;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== contentType) {
    t15 = contentType === "experiences" && <div className="z-floating pointer-events-none absolute right-0 bottom-0 rounded-tl-2xl rounded-br-[15px] bg-linear-to-b from-[#ff7cff] to-[#903bff] p-1.5 lg:rounded-br-lg"><IconExperimence className="size-6 fill-white" /></div>;
    $[34] = contentType;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== contentType) {
    t16 = contentType === "games" && <div className="z-floating pointer-events-none absolute right-0 bottom-0 rounded-tl-2xl rounded-br-[15px] bg-linear-to-b from-[#33c2e9] to-[#3478f6] p-1.5 lg:rounded-br-lg"><IconGamepad className="size-6 fill-white" /></div>;
    $[36] = contentType;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15 || $[44] !== t16) {
    t17 = <div className="relative">{t10}{t11}{t12}{t13}{t14}{t15}{t16}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== room.name) {
    t18 = <h3 className="text-vive-primary-0 mb-0.5 line-clamp-2 min-w-0 overflow-hidden text-[14px] leading-[1.3] font-semibold wrap-break-word lg:mb-1 lg:text-[16px]">{room.name}</h3>;
    $[46] = room.name;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const t19 = `/profile/${room.communityUserId}`;
  let t20;
  if ($[48] !== room.creator || $[49] !== t19) {
    t20 = <Link href={t19} className="text-vive-primary-30 z-floating relative min-w-0 overflow-hidden text-ellipsis transition-colors duration-300 hover:text-white hover:underline">{room.creator}</Link>;
    $[48] = room.creator;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== room.ownerIsVerified) {
    t21 = <VerifiedBadge isVerified={room.ownerIsVerified} />;
    $[51] = room.ownerIsVerified;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t20 || $[54] !== t21) {
    t22 = <p className="body-1 flex items-center gap-1 text-[12px] leading-[1.3] lg:text-[13px]">{t20}{t21}</p>;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <IconEye className="text-vive-primary-30 size-3" />;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== room.views) {
    t24 = <div className="flex items-center gap-1">{t23}<span className="text-vive-primary-20 text-[12px] leading-[1.3]">{room.views}</span></div>;
    $[57] = room.views;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== isVOD || $[60] !== room.likes) {
    t25 = !isVOD && <><IconLike className="text-vive-primary-30 size-3" /><span className="text-vive-primary-20 text-[12px] leading-[1.3]">{room.likes}</span></>;
    $[59] = isVOD;
    $[60] = room.likes;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== formatVODTime || $[63] !== isVOD || $[64] !== room.vodLastUpdate) {
    t26 = isVOD && room.vodLastUpdate && <span className="text-vive-primary-20 text-[12px] leading-[1.3]">{formatVODTime(room.vodLastUpdate)}</span>;
    $[62] = formatVODTime;
    $[63] = isVOD;
    $[64] = room.vodLastUpdate;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t25 || $[67] !== t26) {
    t27 = <div className="flex items-center gap-1">{t25}{t26}</div>;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t24 || $[70] !== t27) {
    t28 = <div className="flex items-center gap-3">{t24}{t27}</div>;
    $[69] = t24;
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== room.description) {
    t29 = <p className="body-1 line-clamp-2 hidden text-sm lg:block lg:max-h-[84px]">{room.description}</p>;
    $[72] = room.description;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t22 || $[75] !== t28 || $[76] !== t29) {
    t30 = <div className="flex flex-col gap-0.5">{t22}{t28}{t29}</div>;
    $[74] = t22;
    $[75] = t28;
    $[76] = t29;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== t18 || $[79] !== t30) {
    t31 = <div className="min-w-0 flex-1">{t18}{t30}</div>;
    $[78] = t18;
    $[79] = t30;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  let t32;
  if ($[81] !== t17 || $[82] !== t31 || $[83] !== t6) {
    t32 = <div className="hover:bg-vive-primary-50/10 group relative flex cursor-pointer items-center gap-4 p-4 transition-all duration-300 hover:rounded-lg hover:shadow-lg lg:p-0" onMouseEnter={t1} onMouseLeave={t2} onTouchStart={t3} onTouchEnd={t4}>{t6}{t17}{t31}</div>;
    $[81] = t17;
    $[82] = t31;
    $[83] = t6;
    $[84] = t32;
  } else {
    t32 = $[84];
  }
  return t32;
}
export function RoomCardSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ViverseSkeleton className="1.5xl:min-w-[306px] aspect-306/178 w-[171px] shrink-0 rounded-[15px] md:w-32 lg:min-w-[200px] lg:rounded-lg" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ViverseSkeleton className="mb-1 h-4 w-40" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ViverseSkeleton className="h-4 w-32" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="group flex cursor-wait items-center gap-4 p-4 lg:p-0">{t0}<div className="min-w-0 flex-1">{t1}<div className="flex flex-col gap-1">{t2}<div className="flex items-center gap-2"><ViverseSkeleton className="h-4 w-16" /><ViverseSkeleton className="h-4 w-16" /></div></div></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}